import React from "react";
import Badge from "../Badge";
import { convertToTitleCase } from "../../Utils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { CalendarIcon } from "@heroicons/react/outline";
import ConsultantCard from "../ConsultantCard";

dayjs.extend(customParseFormat);

const ConsultantTutoringCard = ({ item }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const now = new Date();
  const sessions = item.schedules?.items || [];
  const upcoming = sessions.filter((s) => new Date(s.scheduledAt) > now);
  const upcomingCount = upcoming.length;
  const bookedCount = upcoming.filter((s) => s.status !== "AVAILABLE").length;

  return (
    <div className="flex justify-between flex-col flex-auto gap-4 overflow-hidden">
      <div className="flex flex-col gap-4">
        {item.imageUrl ? (
          <div className="relative rounded-md w-full h-auto aspect-video overflow-hidden">
            <img
              className="absolute object-contain w-full h-full z-10"
              src={item.imageUrl}
              alt="Banner"
              loading="lazy"
            />
            <div
              className="absolute w-full h-full bg-fill blur-[50px] brightness-90 mask-gradient"
              style={{
                backgroundImage: `url('${item.imageUrl}')`,
              }}
            ></div>
          </div>
        ) : (
          <div className="rounded-md w-full h-auto aspect-video text-center overflow-hidden bg-gray-200 text-sm text-gray-500 flex flex-col justify-center">
            {t("No Image")}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Badge title={convertToTitleCase(item.type)} type="success" />
          <Badge title={item.category.name} type="alert" />
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              item.available
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {item.available ? t("Available") : t("Unavailable")}
          </span>
          {item.zoomEnabled && <Badge title={t("Zoom")} />}
          {item.callEnabled && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-600">
              {t("Video Call")}
            </span>
          )}
          {item.callEnabled && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-600">
              {t("Screen Sharing")}
            </span>
          )}
        </div>

        <div>
          <h3 className="text-sm font-bold text-gray-900 text-left line-clamp-2">
            {item.title}
          </h3>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div>
          <ConsultantCard consultant={item.consultant} />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <span>{t("tutoringUpcomingSessions", { count: upcomingCount })}</span>
          {bookedCount > 0 && (
            <span className="text-amber-600">
              {"\u00B7 "}
              {t("tutoringBookedSessions", { count: bookedCount })}
            </span>
          )}
        </div>
        <div className="flex justify-between items-center overflow-hidden">
          <div className="flex flex-col text-sm font-bold text-gray-900 text-left">
            <NumericFormat
              displayType={"text"}
              value={item.price}
              thousandSeparator={"."}
              decimalSeparator={","}
              prefix={"Rp "}
            />
            <p className="text-sm font-normal text-gray-500 text-left">
              {item.duration} {t("Minute")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/manage-tutoring/edit/${item.id}`)}
            className="bg-blue-50 font-medium text-blue-600 rounded-lg text-sm px-4 py-1.5 transition-colors hover:bg-blue-100"
          >
            {t("Edit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsultantTutoringCard;
